import { useState, useEffect } from 'react';
import { useLang } from '../../../hooks/useLang';
import { useDarkMode } from '../../../hooks/useDarkMode';
import type { Lang } from '../../../hooks/useLang';

const stats = [
  { target: 87, suffix: '', zhLabel: '全球真实案例', enLabel: 'Global Cases', icon: 'ri-folder-chart-line', color: '#F59E0B' },
  { target: 12, suffix: '+', zhLabel: '香港持牌VATP', enLabel: 'HK Licensed VATPs', icon: 'ri-building-2-line', color: '#A78BFA' },
  { target: 50, suffix: '+', zhLabel: '名词百科词条', enLabel: 'Glossary Terms', icon: 'ri-book-2-line', color: '#34D399' },
  { target: 26, suffix: '', zhLabel: '全行业上链指南', enLabel: 'Industry Guides', icon: 'ri-grid-line', color: '#FB923C' },
];

export default function Hero() {
  const [counts, setCounts] = useState(stats.map(() => 0));
  const { lang, t, isRTL } = useLang();
  const { isDark } = useDarkMode();

  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const stepTime = duration / steps;
    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = Math.min(step / steps, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCounts(stats.map((s) => Math.round(s.target * eased)));
      if (step >= steps) clearInterval(timer);
    }, stepTime);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="relative min-h-screen flex flex-col justify-center overflow-hidden">
      <div
        className="absolute inset-0 transition-all duration-500"
        style={{
          background: isDark
            ? 'linear-gradient(135deg, #0A0015 0%, #120028 30%, #1E0A4A 65%, #2D1570 100%)'
            : 'linear-gradient(135deg, #1A0050 0%, #2E1065 30%, #4C1D95 65%, #6D28D9 100%)',
        }}
      />
      <div className="absolute inset-0 opacity-10" style={{ backgroundImage: `linear-gradient(#A78BFA 1px, transparent 1px), linear-gradient(90deg, #A78BFA 1px, transparent 1px)`, backgroundSize: '60px 60px' }} />
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#7C3AED]/30 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/3 w-64 h-64 bg-[#F59E0B]/15 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-1/2 right-1/4 w-80 h-80 bg-[#4C1D95]/40 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-24 right-24 w-4 h-4 bg-[#F59E0B] rounded-full animate-pulse opacity-80" />
      <div className="absolute top-1/3 left-16 w-3 h-3 bg-[#A78BFA] rounded-full animate-pulse opacity-60" style={{ animationDelay: '1s' }} />
      <div className="absolute bottom-32 right-1/3 w-2 h-2 bg-white rounded-full opacity-50" />
      <div className="absolute top-2/3 right-16 w-3 h-3 bg-[#F59E0B] rounded-full opacity-40 animate-pulse" style={{ animationDelay: '2s' }} />

      <div className="relative z-10 w-full max-w-[1440px] mx-auto px-4 lg:px-8 pt-28 pb-20" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className={`grid lg:grid-cols-2 gap-12 lg:gap-16 items-center ${isRTL ? 'lg:grid-flow-dense' : ''}`}>
          {/* Left Content (or Right in RTL) */}
          <div className={isRTL ? 'text-right' : ''}>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#F59E0B]/15 border border-[#F59E0B]/30 rounded-full mb-6">
              <span className="w-2 h-2 bg-[#F59E0B] rounded-full animate-pulse shrink-0" />
              <span className="text-[#F59E0B] text-sm font-semibold tracking-wide">{t('2026 RWA元年', '2026 RWA Era')}</span>
              <span className="text-white/50 text-xs">{t('· 合规知识门户', '· Compliance Knowledge Portal')}</span>
            </div>

            <h1 className="text-4xl md:text-5xl xl:text-6xl font-black text-white leading-[1.15] mb-6">
              {lang === 'zh' ? (
                <>看懂<span className="text-[#F59E0B]"> RWA</span><br /><span className="text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(90deg, #C4B5FD, #F59E0B)' }}>上 RWA99</span></>
              ) : lang === 'ja' ? (
                <>RWAを<span className="text-[#F59E0B]">理解する</span><br /><span className="text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(90deg, #C4B5FD, #F59E0B)' }}>RWA99へ</span></>
              ) : lang === 'ar' ? (
                <>افهم<span className="text-[#F59E0B]"> RWA</span><br /><span className="text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(90deg, #C4B5FD, #F59E0B)' }}>زر RWA99</span></>
              ) : lang === 'ko' ? (
                <>RWA<span className="text-[#F59E0B]"> 이해하기</span><br /><span className="text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(90deg, #C4B5FD, #F59E0B)' }}>RWA99 방문</span></>
              ) : lang === 'ru' ? (
                <>Понять<span className="text-[#F59E0B]"> RWA</span><br /><span className="text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(90deg, #C4B5FD, #F59E0B)' }}>RWA99</span></>
              ) : lang === 'de' ? (
                <>RWA<span className="text-[#F59E0B]"> Verstehen</span><br /><span className="text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(90deg, #C4B5FD, #F59E0B)' }}>RWA99 Besuchen</span></>
              ) : lang === 'fr' ? (
                <>Comprendre<span className="text-[#F59E0B]"> RWA</span><br /><span className="text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(90deg, #C4B5FD, #F59E0B)' }}>Visitez RWA99</span></>
              ) : lang === 'es' ? (
                <>Entiende<span className="text-[#F59E0B]"> RWA</span><br /><span className="text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(90deg, #C4B5FD, #F59E0B)' }}>Visita RWA99</span></>
              ) : lang === 'pt' ? (
                <>Entenda<span className="text-[#F59E0B]"> RWA</span><br /><span className="text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(90deg, #C4B5FD, #F59E0B)' }}>Visite RWA99</span></>
              ) : (
                <>Understand<span className="text-[#F59E0B]"> RWA</span><br /><span className="text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(90deg, #C4B5FD, #F59E0B)' }}>Visit RWA99</span></>
              )}
            </h1>

            <p className="text-white/70 text-base lg:text-lg leading-relaxed mb-3 max-w-lg">
              {t('hero.subtitle')}
            </p>
            <p className="text-white/40 text-sm mb-8">
              {t('⚠ 本站仅供科普，不募资、不推介、不交易', '⚠ For educational purposes only — no fundraising, no solicitation, no trading')}
            </p>

            <div className={`flex flex-wrap gap-2 mb-8 ${isRTL ? 'justify-end' : ''}`}>
              {(lang === 'zh' ? '合规路径,零基础入门,87个案例,持牌机构,落地SOP,名词百科,政策解读,行业指南'
                : lang === 'fr' ? 'Conformité,Débutants,87 Cas,Agréés,SOP,Glossaire,Politique,Guide'
                : lang === 'es' ? 'Cumplimiento,Principiantes,87 Casos,Licenciados,SOP,Glosario,Política,Guía'
                : lang === 'pt' ? 'Conformidade,Iniciantes,87 Casos,Licenciados,SOP,Glossário,Política,Guia'
                : lang === 'ja' ? 'コンプライアンス,初心者,87事例,認可機関,SOP,用語集,政策,ガイド'
                : lang === 'de' ? 'Compliance,Anfänger,87 Fälle,Lizenziert,SOP,Glossar,Politik,Leitfaden'
                : lang === 'ko' ? '컴플라이언스,초보자,87 사례,인가기관,SOP,용어집,정책,가이드'
                : lang === 'ar' ? 'امتثال,مبتدئون,87 حالة,مرخصون,SOP,مسرد,سياسة,دليل'
                : lang === 'ru' ? 'Соответствие,Начинающие,87 Случаев,Лицензиаты,SOP,Глоссарий,Политика,Руководство'
                : 'Compliance Path,Beginner Guide,87 Cases,Licensed Institution,Enterprise SOP,Glossary,Policy Analysis,Industry Guide'
              ).split(',').map((tag) => (
                <span key={tag} className="inline-flex items-center gap-1 px-3 py-1.5 bg-white/8 border border-white/15 text-white/80 text-xs rounded-full hover:border-[#F59E0B]/50 hover:text-[#F59E0B] transition-all cursor-default">
                  <i className="ri-check-line text-[#A78BFA] text-xs"></i>
                  {tag}
                </span>
              ))}
            </div>

            <div className={`flex flex-col sm:flex-row gap-3 ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
              <a href="#section-preview" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 font-bold rounded-full whitespace-nowrap cursor-pointer text-sm transition-all hover:scale-105" style={{ background: 'linear-gradient(135deg, #F59E0B, #FB923C)', color: '#1A0050', boxShadow: '0 8px 32px rgba(245,158,11,0.4)' }}>
                <i className="ri-rocket-line"></i>
                {t('立即探索全部板块', 'Explore All Sections')}
              </a>
              <a href="#rwa-timeline" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 border border-white/25 text-white font-medium rounded-full hover:bg-white/10 hover:border-white/40 transition-all whitespace-nowrap cursor-pointer text-sm">
                <i className="ri-history-line"></i>
                {t('RWA发展历程', 'RWA History')}
              </a>
              <a href="#cases" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 border border-[#7C3AED]/50 text-[#C4B5FD] font-medium rounded-full hover:bg-[#7C3AED]/20 transition-all whitespace-nowrap cursor-pointer text-sm">
                <i className="ri-folder-chart-line"></i>
                {t('浏览87个案例', 'Browse 87 Cases')}
              </a>
            </div>
            {/* RTL support for Arabic */}
            <style>{`[dir="rtl"] .hero-tags { direction: rtl; }`}</style>
          </div>

          {/* Right: Stats */}
          <div className="grid grid-cols-2 gap-4">
            {stats.map((s, i) => (
              <div key={i} className={`relative backdrop-blur-sm border rounded-2xl p-6 transition-all group overflow-hidden ${isDark ? 'bg-white/3 border-white/8 hover:bg-white/7 hover:border-white/15' : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20'}`}>
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: `linear-gradient(135deg, ${s.color}08, transparent)` }} />
                <div className="w-12 h-12 flex items-center justify-center rounded-xl mb-4" style={{ background: `${s.color}20`, border: `1px solid ${s.color}30` }}>
                  <i className={`${s.icon} text-2xl`} style={{ color: s.color }}></i>
                </div>
                <div className="text-3xl xl:text-4xl font-black text-white mb-1">{counts[i]}{s.suffix}</div>
                <div className="text-white/55 text-sm">{t(s.zhLabel, s.enLabel)}</div>
              </div>
            ))}
            <div className="col-span-2 rounded-2xl p-5 flex items-center gap-4" style={{ background: isDark ? 'linear-gradient(135deg, rgba(245,158,11,0.10), rgba(245,158,11,0.03))' : 'linear-gradient(135deg, rgba(245,158,11,0.15), rgba(245,158,11,0.05))', border: `1px solid ${isDark ? 'rgba(245,158,11,0.20)' : 'rgba(245,158,11,0.25)'}` }}>
              <div className="w-12 h-12 flex items-center justify-center bg-[#F59E0B] rounded-xl shrink-0">
                <i className="ri-shield-check-fill text-[#1A0050] text-2xl"></i>
              </div>
              <div>
                <div className="text-white font-bold text-sm mb-0.5">{t('合规第一，科普为本', 'Compliance First, Education-Driven')}</div>
                <div className="text-white/55 text-xs leading-relaxed">
                  {t(
                    '境内禁止虚拟货币交易 · RWA仅限香港合格投资者 · 本站不募资不推介不交易',
                    'No crypto trading on mainland · RWA for HK qualified investors only · No fundraising or solicitation'
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 animate-bounce">
        <span className="text-white/30 text-xs">{t('向下滚动', 'Scroll Down')}</span>
        <i className="ri-arrow-down-line text-xl text-white/30"></i>
      </div>
    </section>
  );
}
